// ============================================================================
// PROCESS HEALTH — MAIN COMPONENT
// Executive Assessment generate + output.
// Period comparison has moved to the Trend Analysis tab.
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { getAIModelConfig, getInvokeAIProvider } from '@advisor-core/lib/aiModelConfig';
import { invokeWebAI } from '@advisor-core/lib/webAiClient';
import { Activity } from 'lucide-react';
import {
  getModelMetrics, getModelMetricsTotal, getAppVersion,
  getIssueTypes, getProcessModelTitle, taskJSON,
  getModelID, getFieldUsage, getStatusTypeSummary, getStatusTypeMappings, getAiConsultantContent,
  getAttributePrivacyRules, getActivityType, getActivityContext,
} from '@process-advisor/process-miner/processMiner/processMiner';
import { applySampleItemPrivacy, AttributePrivacyPanel } from '@process-advisor/process-miner/consultant/attributePrivacyUtils';
import AiDataPanel, { AiDataButton } from '@process-advisor/process-miner/consultant/AiDataPanel';
import AiReportScopePanel, { AiScopeButton } from '@process-advisor/process-miner/consultant/AiReportScopePanel';
import { loadAiReportScopeLocal, loadAiReportScopeBackend, normalizeAiReportScope, applyAiReportScopeToEvidence } from '@process-advisor/process-miner/consultant/aiReportScope';

import { openProgressModal }   from '@process-advisor/process-miner/api/methods/modals/openProgressModal';
import { closeProgressModal }  from '@process-advisor/process-miner/api/methods/modals/closeProgressModal';
import { updateProgressModal } from '@process-advisor/process-miner/api/methods/modals/updateProgressModal';
import { startAiProgressTimer, stopAiProgressTimer, stopAllAiProgressTimers } from '@process-advisor/process-miner/consultant/aiProgressModalTimer';
import { apiProvider }         from '@process-advisor/process-miner/api/apiProvider';

import { buildProcessHealthPrompt }            from '@process-advisor/process-miner/consultant/processHealth-prompt';
import { buildImprovementOpportunitiesInputEvidence } from '@process-advisor/process-miner/consultant/consultantPromptDataNormalizer';
import { renderProcessHealthHTML, buildProcessHealthExportHTML } from '@process-advisor/process-miner/consultant/processHealth-renderer';
import ExportModal        from '@process-advisor/process-miner/consultant/ExportModal';
import { themeAiHtml, appDefaultReportTheme } from '@process-advisor/process-miner/consultant/reportThemeUtils';
import ConsultantQaPanel, { AskAiButton } from '@process-advisor/process-miner/consultant/ConsultantQaPanel';
import ConsultantTabHeader, { ReadyBadge, HEADER_META_TEXT_STYLE, getHeaderPrimaryButtonStyle, getHeaderSecondaryButtonStyle } from '@process-advisor/process-miner/consultant/ConsultantTabHeader';

import { replaceQuotes, sleep } from '@process-advisor/process-miner/dashboard/aiinsights/aiInsights-utils';
import { saveConsultantContent } from '@process-advisor/process-miner/consultant/saveConsultantContent';
import {
  getCachedConsultantContent, setCachedConsultantContent,
  isHtmlCurrentVersion,
  getCachedConsultantJson, setCachedConsultantJson,
} from '@process-advisor/process-miner/consultant/consultantContentState';
import HtmlPreviewFrame from '@process-advisor/process-miner/consultant/HtmlPreviewFrame';
import { SaveSnapshotButton, SaveSnapshotModal } from '@process-advisor/process-miner/consultant/ConsultantSnapshotManager';
import { saveSnapshot } from '@process-advisor/process-miner/consultant/consultantSnapshotService';
import { injectOrganizationContext } from '@process-advisor/process-miner/consultant/organizationProfile';
import { buildSharedConsultantPayload } from '@process-advisor/process-miner/consultant/consultantSharedAiPayload';
import { buildConsultantRuntimeArgs } from '@process-advisor/process-miner/consultant/consultantSharedRuntimeArgs';
import { validateHealthScoreAgainstNarrative } from '@process-advisor/process-miner/consultant/consultantHealthScoreValidator';
import { invokeProcessOpenAIWithBilling } from '@process-advisor/process-miner/consultant/aiBillingAccess';

// ── Forge lazy loader ──────────────────────────────────────────────────────────
let forgeModule = null;
const loadForgeModule = async () => {
  if (!forgeModule) forgeModule = await import('@forge/bridge');
  return forgeModule;
};

const toInvokeProvider = (p) =>
  p === 'gemini' ? 'Gemini' : p === 'claude' ? 'Claude' : 'ChatGPT';

const SLEEP_MS = 50;
const POLL_MS  = 1500;
const COL_ASSESSMENT = 'ai_process_health_assessment';


const cleanAndParseJSON = (raw) => {
  let s = (raw || '{}')
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();

  const firstObject = s.indexOf('{');
  if (firstObject > 0) s = s.slice(firstObject);

  try { return JSON.parse(s); } catch (_) {}

  // If Gemini returns trailing text after a valid JSON object, trim it.
  const lastBrace = s.lastIndexOf('}');
  if (lastBrace !== -1) {
    const trimmed = s.substring(0, lastBrace + 1);
    try { return JSON.parse(trimmed); } catch (_) {}
  }

  // Final defensive repair for a near-complete object. This is intentionally
  // conservative: it removes a dangling incomplete property/string, balances
  // brackets, and then lets JSON.parse validate the result.
  let repaired = s
    .replace(/,\s*$/, '')
    .replace(/,\s*"[^"]*"?\s*:?\s*[^,\}\]]*$/, '');

  const stack = [];
  let inString = false;
  let escaped = false;
  for (const ch of repaired) {
    if (escaped) { escaped = false; continue; }
    if (ch === '\\') { escaped = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (ch === '{' || ch === '[') stack.push(ch);
    else if (ch === '}' || ch === ']') stack.pop();
  }
  if (inString) repaired += '"';
  for (let i = stack.length - 1; i >= 0; i--) repaired += stack[i] === '{' ? '}' : ']';

  return JSON.parse(repaired);
};

// ── Date helpers ───────────────────────────────────────────────────────────────
const today     = () => new Date().toISOString().slice(0, 10);
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return d.toISOString().slice(0, 10); };
const toFilter  = (iso) => iso.replaceAll('-', '/');

const inputStyle = {
  height: 32, border: '1px solid #DFE1E6', borderRadius: 4,
  padding: '0 10px', fontSize: 13, background: '#fff',
};

const mergeEvidenceCoverage = (json = {}, inputPayload = {}) => {
  const canonicalCoverage = inputPayload?.data_coverage || {};
  const currentCoverage = json?.data_coverage || {};
  return {
    ...json,
    data_coverage: {
      ...currentCoverage,
      process_metrics: currentCoverage.process_metrics === true || canonicalCoverage.process_metrics === true,
      transitions: currentCoverage.transitions === true || canonicalCoverage.transitions === true,
      variants: currentCoverage.variants === true || canonicalCoverage.variants === true,
      field_usage: currentCoverage.field_usage === true || canonicalCoverage.field_usage === true,
      status_types: currentCoverage.status_types === true || canonicalCoverage.status_types === true,
      time_intelligence: currentCoverage.time_intelligence === true || canonicalCoverage.time_intelligence === true,
      sample_items: currentCoverage.sample_items === true || canonicalCoverage.sample_items === true,
      sla_metrics: currentCoverage.sla_metrics === true || canonicalCoverage.sla_metrics === true,
      source_summary: currentCoverage.source_summary || canonicalCoverage.evidence_base_label || '',
      sample_item_count: currentCoverage.sample_item_count ?? canonicalCoverage.sample_size ?? null,
      status_types_count: currentCoverage.status_types_count ?? canonicalCoverage.status_types_count ?? null,
    },
  };
};



// ── Main component ─────────────────────────────────────────────────────────────

const ProcessHealth = ({ processLabel = 'Process', entityLabel = 'Work Item', refreshKey, aiModelConfig }) => {

  const modelId = getModelID();

  // ── Assessment state ────────────────────────────────────────────────────────
  const [isGenerating, setIsGenerating]     = useState(false);
  const [showAiDataPanel, setShowAiDataPanel] = useState(false);
  const [showAiScopePanel, setShowAiScopePanel] = useState(false);
  const [aiReportScope, setAiReportScope] = useState(() => normalizeAiReportScope(loadAiReportScopeLocal(modelId, 'processHealth') || {}));
  const [aiReportScopeSource, setAiReportScopeSource] = useState(() => loadAiReportScopeLocal(modelId, 'processHealth') ? 'local' : 'none');
  const effectiveAiReportScope = normalizeAiReportScope(aiReportScope || {});
  const [generated, setGenerated]           = useState(false);
  const [assessmentHtml, setAssessmentHtml] = useState(null);
  const [generationTime, setGenerationTime] = useState(null);
  const [requestId, setRequestId]           = useState(null);
  const [forgeStatus, setForgeStatus]       = useState(null);
  const pollRef = useRef(null);
  const progressSecondsRef = useRef(0);
  const aiProgressTimerRef = useRef(null);
  const requestDebugRef = useRef(null);

  const [isExportModalOpen, setIsExportModalOpen]   = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [snapshotSaving, setSnapshotSaving] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);

  // Load report scope from backend when available; localStorage is used as an immediate fallback.
  useEffect(() => {
    let cancelled = false;
    loadAiReportScopeBackend(modelId, 'processHealth').then((remote) => {
      if (cancelled) return;
      if (remote?.scope) {
        setAiReportScope(normalizeAiReportScope(remote.scope));
        setAiReportScopeSource(remote.source || 'model');
      } else {
        const local = loadAiReportScopeLocal(modelId, 'processHealth');
        setAiReportScope(normalizeAiReportScope(local || {}));
        setAiReportScopeSource(local ? 'local' : 'none');
      }
    });
    return () => { cancelled = true; };
  }, [modelId]);

  const handleAiScopeChange = (nextScope, sourceHint) => {
    setAiReportScope(normalizeAiReportScope(nextScope));
    if (sourceHint) setAiReportScopeSource(sourceHint);
  };

  const getAiScopeButtonState = () => {
    if (!effectiveAiReportScope.enabled) return 'off';
    if (aiReportScopeSource === 'global') return 'default';
    return 'report';
  };

  // Load cached on mount — session cache first, then DB

  useEffect(() => {
    const cached = getCachedConsultantContent({ modelId, column: COL_ASSESSMENT });
    const dbHtml = getAiConsultantContent ? getAiConsultantContent(COL_ASSESSMENT) : null;
    const saved = cached || (isHtmlCurrentVersion(dbHtml) ? dbHtml : null);
    if (saved) {
      setAssessmentHtml(saved);
      setGenerated(true);
      if (!cached) setCachedConsultantContent({ modelId, column: COL_ASSESSMENT, html: saved });
    }
  }, [modelId]);

  // ── Forge poll loop — mirrors executiveAssessment.jsx exactly ──────────────
  // Polls every 1s, handles 'pending' and 'processing' states, increments a
  // counter for the progress modal seconds display.
  useEffect(() => {
    if (!requestId || !isGenerating) return;
    if (pollRef.current) clearInterval(pollRef.current);

    pollRef.current = setInterval(async () => {
      try {
        const { invoke } = await loadForgeModule();
        const sd = await invoke('checkStatus', { requestId });
        setForgeStatus(sd);
        progressSecondsRef.current += 1;
        updateProgressModal({ status: 'Generating assessment — ' + progressSecondsRef.current + 's', ai: true });

        if (sd?.status === 'completed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          const raw  = sd?.result || '{}';
          const json = cleanAndParseJSON(typeof raw === 'string' ? raw : JSON.stringify(raw));
          replaceQuotes(json);
          const validatedJson = mergeEvidenceCoverage(validateHealthScoreAgainstNarrative(json, requestDebugRef.current?.inputPayload || null), requestDebugRef.current?.inputPayload || null);
          console.log('[PA Health Debug][Process][completed]', {
                  signature: requestDebugRef.current?.signature || null,
            canonicalHealth: requestDebugRef.current?.canonicalHealth || null,
            actualOverallHealth: validatedJson?.overall_health || json?.overall_health || null,
            forgeStatus: sd?.status || null,
          });
          setGenerationTime(progressSecondsRef.current.toString());
          const html = themeAiHtml(renderProcessHealthHTML(validatedJson, getProcessModelTitle()), appDefaultReportTheme());
          setAssessmentHtml(html); setGenerated(true);
          setCachedConsultantContent({ modelId, column: COL_ASSESSMENT, html });
          setCachedConsultantJson({ modelId, column: COL_ASSESSMENT, json: validatedJson });
          saveConsultantContent({ modelId, column: COL_ASSESSMENT, html, AppVersion: getAppVersion() });
          setIsGenerating(false);
          closeProgressModal();
        }
        if (sd?.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setIsGenerating(false); closeProgressModal();
        }
      } catch (err) {
        console.error('[ProcessHealth] Poll error:', err);
        clearInterval(pollRef.current);
        pollRef.current = null;
        setIsGenerating(false); closeProgressModal();
      }
    }, 1000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [requestId, isGenerating, modelId]);

  // ── Data preparation ────────────────────────────────────────────────────────
  const prepareTransitions = () => {
    const src = apiProvider.processStepsTable() || [];
    return src.slice(0, 40).map((t) => ({
      Transition:            t.step,
      'From Activity':       t.from_activity,
      'To Activity':         t.to_activity,
      'Work items':          t.tasks,
      'Avg deviation (hrs)': t.avg_duration_hrs?.toFixed(2) ?? 'N/A',
      'Std deviation (hrs)': t.std_deviation_hrs?.toFixed(2) ?? 'N/A',
      [`% total ${apiProvider.getEntityUpper()}s`]:
        t.perc_total_tasks != null ? (t.perc_total_tasks * 100).toFixed(2) : 'N/A',
      'Is rework?': t.loop,
    }));
  };

  const prepareVariants = () => {
    const src = apiProvider.variantsTable() || [];
    return src.slice(0, 30).map((v) => ({
      Variant:  v.variant,
      ID:       v.variant_id,
      [`${apiProvider.getEntityUpper()}s`]: v.tasks,
      [`% Total ${apiProvider.getEntityUpper()}s`]:
        v.perc_total_tasks != null ? (Number(v.perc_total_tasks) * 100).toFixed(2) : 'N/A',
      'Is Rework':           v.loop,
      Loops:                 v.loops,
      'Std Deviation (hrs)': v.std_deviation_hrs != null ? Number(v.std_deviation_hrs).toFixed(2) : 'N/A',
      'Avg Duration (hrs)':  v.avg_duration_hrs  != null ? Number(v.avg_duration_hrs).toFixed(2)  : 'N/A',
    }));
  };

  const prepareMetrics = () =>
    getModelMetrics().filter(m => m.work_items > 0).map(m => ({
      [`${apiProvider.getEntityUpper()}s`]: m.work_items,
      Variants:              m.variants,
      Activities:            m.activities,
      Steps:                 m.steps,
      'Avg Transitions':     m.avg_transitions.toFixed(1),
      'Avg Duration':        (m.cycle_time    / 3600).toFixed(2),
      'Std Deviation':       (m.std_deviation / 3600).toFixed(2),
      '% Rework':            (m.rework_rate   * 100 ).toFixed(2),
      avg_flow_efficiency:   m.avg_flow_efficiency ?? null,
    }));

  const buildArgs = () => {
    const { runtimeArgs, debug } = buildConsultantRuntimeArgs({
      issueTypes: getIssueTypes(),
      metrics: getModelMetrics(),
      metricsTotal: typeof getModelMetricsTotal === 'function' ? getModelMetricsTotal() : null,
      processSteps: apiProvider.processStepsTable() || [],
      variants: apiProvider.variantsTable() || [],
      rawSampleItems: taskJSON(1000),
      reportScope: effectiveAiReportScope,
      privacyRules: getAttributePrivacyRules(),
      fieldUsage: getFieldUsage ? getFieldUsage() : {},
      statusTypeSummary: getStatusTypeSummary ? getStatusTypeSummary() : null,
      statusTypeMappings: getStatusTypeMappings ? getStatusTypeMappings() : [],
      activityType: typeof getActivityType === 'function' ? getActivityType() : null,
      activityContext: typeof getActivityContext === 'function' ? getActivityContext() : null,
      extra: { roiEnabled: false },
    });
    const inputPayload = buildSharedConsultantPayload(runtimeArgs);
    return { ...runtimeArgs, inputPayload, debug, privacy: debug.privacy, reportScope: debug.reportScope };
  };



  // ── Generate ────────────────────────────────────────────────────────────────
  const handleSaveSnapshot = async (description) => {
    setSnapshotSaving(true);
    try {
      const html = assessmentHtml || null;
      if (!html) return;
      const result = await saveSnapshot({
        modelId, tab: 'processHealth', tabLabel: 'Process Health',
        description, html, AppVersion: getAppVersion(),
      });
      if (!result?.success) throw new Error('Snapshot was not saved');
      setSnapshotModalOpen(false);
    } catch (error) {
      console.error('Advisor snapshot save failed', error);
      window.alert(`Unable to save Advisor Snapshot: ${error.message || error}`);
    } finally {
      setSnapshotSaving(false);
    }
  };

  const generate = async () => {
    if (isGenerating) return;
    const startTime = Date.now();
    setIsGenerating(true);
    setAssessmentHtml(null); setGenerated(false); setGenerationTime(null);
    progressSecondsRef.current = 0;
    stopAllAiProgressTimers();

    const AppVersion = getAppVersion();
    await openProgressModal({ status: 'Generating Process Health Assessment…' });
    await sleep(SLEEP_MS);

    const args = buildArgs();
    requestDebugRef.current = {
      signature: args.debug.signature,
      canonicalHealth: args.inputPayload?.canonical_health || null,
      summary: args.debug.summary,
      inputPayload: args.inputPayload || null,
    };
    updateProgressModal({ status: 'Generating Process Health Assessment…', ai: true });
    const prompt = await injectOrganizationContext(buildProcessHealthPrompt(args));
    const { provider, model, temperature } = getAIModelConfig(aiModelConfig);

    // Temporary diagnostic: log the exact Process Health AI prompt before it is sent.
    // This is intentionally placed after organisation context injection so the
    // console output matches the prompt used by both Forge and ServiceNow paths.
    console.log('[ProcessHealth][AI Prompt]', {
      provider: toInvokeProvider(provider),
      model,
      temperature,
      prompt,
    });

    if (AppVersion === 'FORGE') {
      try {
        const { invoke } = await loadForgeModule();
        const response   = await invokeProcessOpenAIWithBilling({
          invoke,
          reportLabel: 'Process Health',
          payload: { prompt, aiProvider: toInvokeProvider(provider), model, temperature },
        });
        setRequestId(response.requestId);
        setForgeStatus({ status: 'pending' });
      } catch (err) {
        console.error('[ProcessHealth] Forge error:', err);
        setIsGenerating(false); closeProgressModal();
      }
      return;
    }

    // Direct API path
    if (AppVersion === 'WEB') {
      aiProgressTimerRef.current = await startAiProgressTimer('Generating Process Health');
      const startTime = Date.now();
      try {
        const result = await invokeWebAI({
          prompt,
          reportLabel: 'Process Health',
          temperature,
          responseFormat: 'json',
          modelId,
        });
        const json = cleanAndParseJSON(result?.text || '{}');
        replaceQuotes(json);
        const validatedJson = mergeEvidenceCoverage(
          validateHealthScoreAgainstNarrative(json, requestDebugRef.current?.inputPayload || null),
          requestDebugRef.current?.inputPayload || null
        );
        setGenerationTime(((Date.now() - startTime) / 1000).toFixed(1));
        const html = themeAiHtml(renderProcessHealthHTML(validatedJson, getProcessModelTitle()), appDefaultReportTheme());
        setAssessmentHtml(html); setGenerated(true);
        setCachedConsultantContent({ modelId, column: COL_ASSESSMENT, html });
        setCachedConsultantJson({ modelId, column: COL_ASSESSMENT, json: validatedJson });
        saveConsultantContent({ modelId, column: COL_ASSESSMENT, html, AppVersion });
      } catch (err) {
        console.error('[ProcessHealth] Web AI error:', err);
      } finally {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        setIsGenerating(false); closeProgressModal();
      }
      return;
    }

    if (AppVersion === 'SNOW') {
      const _geminiKey = (typeof API_KEY !== 'undefined' && API_KEY) ? API_KEY : undefined;
      aiProgressTimerRef.current = await startAiProgressTimer('Generating Process Health Assessment');
      try {
        const res  = await fetch(
          `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${_geminiKey}`,
          {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              contents: [{ parts: [{ text: prompt + '\n\nYou must output valid JSON. Do not include ``` at beginning and end' }] }],
              generationConfig: { responseMimeType: 'application/json', maxOutputTokens: 32768 },
            }),
          }
        );
        const data = await res.json();
        const candidate = data?.candidates?.[0] || {};
        const finishReason = candidate?.finishReason || candidate?.finish_reason || '';
        const rawText = candidate?.content?.parts?.[0]?.text || '{}';
        if (!res.ok || data?.error) {
          throw new Error(data?.error?.message || `Gemini request failed (${res.status})`);
        }
        if (String(finishReason).toUpperCase() === 'MAX_TOKENS') {
          throw new Error('Gemini stopped because the response reached the output token limit. Please retry; the request now allows a larger JSON response.');
        }
        const json = cleanAndParseJSON(rawText);
        replaceQuotes(json);
        const validatedJson = mergeEvidenceCoverage(validateHealthScoreAgainstNarrative(json, requestDebugRef.current?.inputPayload || null), requestDebugRef.current?.inputPayload || null);
        console.log('[PA Health Debug][Process][completed]', {
              signature: requestDebugRef.current?.signature || null,
          canonicalHealth: requestDebugRef.current?.canonicalHealth || null,
          actualOverallHealth: validatedJson?.overall_health || json?.overall_health || null,
          forgeStatus: 'completed-dc',
        });
        setGenerationTime(((Date.now() - startTime) / 1000).toFixed(1));
        const html = themeAiHtml(renderProcessHealthHTML(validatedJson, getProcessModelTitle()), appDefaultReportTheme());
        setAssessmentHtml(html); setGenerated(true);
        setCachedConsultantContent({ modelId, column: COL_ASSESSMENT, html });
        setCachedConsultantJson({ modelId, column: COL_ASSESSMENT, json: validatedJson });
        saveConsultantContent({ modelId, column: COL_ASSESSMENT, html, AppVersion });
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        setIsGenerating(false); closeProgressModal();
      } catch (err) {
        stopAiProgressTimer(aiProgressTimerRef.current); aiProgressTimerRef.current = null;
        console.error('[ProcessHealth] direct API error:', err);
        setIsGenerating(false); closeProgressModal();
      }
    }
  };

  // ── Export handlers ────────────────────────────────────────────────────────

  const handleExportHTML = (themeId = appDefaultReportTheme()) => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL_ASSESSMENT });
    const html = cachedJson
      ? themeAiHtml(buildProcessHealthExportHTML(cachedJson, getProcessModelTitle()), themeId)
      : assessmentHtml;
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `process-health-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.html`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setIsExportModalOpen(false);
  };

  const handleExportPDF = (themeId = appDefaultReportTheme()) => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL_ASSESSMENT });
    const html = cachedJson
      ? themeAiHtml(buildProcessHealthExportHTML(cachedJson, getProcessModelTitle()), themeId)
      : assessmentHtml;
    if (!html) return;
    const win = window.open('', '_blank', 'width=1400,height=900');
    if (!win) return;
    win.document.open(); win.document.write(html); win.document.close();
    win.focus(); setTimeout(() => win.print(), 300);
    setIsExportModalOpen(false);
  };

  const handleExportJSON = () => {
    const cachedJson = getCachedConsultantJson({ modelId, column: COL_ASSESSMENT });
    if (!cachedJson) return;
    const blob = new Blob([JSON.stringify(cachedJson, null, 2)], { type: 'application/json;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `process-health-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setIsExportModalOpen(false);
  };

  const handleExportPPTX = async (themeId = appDefaultReportTheme()) => {
    const contentJson = getCachedConsultantJson({ modelId, column: COL_ASSESSMENT });
    if (!contentJson) return;
    try {
      // Lazy import keeps pptxgenjs out of the main bundle until first use.
      const { buildAiContentPptx } = await import('@process-advisor/process-miner/consultant/aiContent-pptx');
      const pptx = buildAiContentPptx('processHealth', contentJson, getProcessModelTitle(), { theme: themeId });
      await pptx.writeFile({ fileName: `process-health-${(getProcessModelTitle() || 'report').replace(/\s+/g, '-').toLowerCase()}.pptx` });
    } catch (error) {
      console.error('PPTX export failed', error);
    }
    setIsExportModalOpen(false);
  };


  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ backgroundColor: '#f8f9fa', padding: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>

        {/* ── Header bar ── */}
        <ConsultantTabHeader
          title="Process Health"
          subtitle="AI-generated leadership view of process health, discovery rules, and priorities"
          icon={<Activity size={20} color="white" />}
          iconBackground="linear-gradient(135deg, #0052CC 0%, #2684FF 100%)"
          titleColor="#0052CC"
          actions={(
            <>
              {!generated && <ReadyBadge color="#0C66E4" />}
              <AiScopeButton
                onClick={() => { setShowAiScopePanel(v => !v); if (showAiDataPanel) setShowAiDataPanel(false); }}
                state={getAiScopeButtonState()}
                count={(effectiveAiReportScope.excludeVariantIds?.length || 0) + (effectiveAiReportScope.excludeWorkItems?.length || 0) + (effectiveAiReportScope.excludePaths?.length || 0) + (effectiveAiReportScope.excludeFieldValues?.length || 0) + (effectiveAiReportScope.instructions?.trim() ? 1 : 0) + ((Number(effectiveAiReportScope.sampleItemLimit) || 100) !== 100 ? 1 : 0)}
              />
              <AiDataButton
                onClick={() => { setShowAiDataPanel(v => !v); if (showAiScopePanel) setShowAiScopePanel(false); }}
                excludedCount={Object.values(getAttributePrivacyRules()).filter(v=>v==='exclude').length}
                anonymizedCount={Object.values(getAttributePrivacyRules()).filter(v=>v==='anonymize').length}
                isActive={showAiDataPanel}
              />
              {generated && (
                <>
                  <AskAiButton hasContent={generated} onClick={() => setQaOpen(true)} />
                  <SaveSnapshotButton color="#0052CC" onClick={() => setSnapshotModalOpen(true)} />
                  <button
                    style={getHeaderPrimaryButtonStyle('#0052CC')}
                    onClick={() => { setAssessmentHtml(null); setGenerated(false); setGenerationTime(null); }}
                  >
                    New Assessment
                  </button>
                  <button
                    style={getHeaderSecondaryButtonStyle('#0052cc')}
                    onClick={() => setIsExportModalOpen(true)}
                  >
                    Export...
                  </button>
                </>
              )}
            </>
          )}
        />
        <AiReportScopePanel
          isOpen={showAiScopePanel}
          onClose={() => setShowAiScopePanel(false)}
          modelId={modelId}
          reportKey="processHealth"
          reportLabel="Process Health assessment"
          scope={effectiveAiReportScope}
          scopeSource={aiReportScopeSource}
          onScopeChange={handleAiScopeChange}
          onScopeSaved={handleAiScopeChange}
          getTaskRows={taskJSON}
          getVariants={() => apiProvider.variantsTable() || []}
          getProcessSteps={() => apiProvider.processStepsTable() || []}
        />
        <AiDataPanel
          isOpen={showAiDataPanel}
          onClose={() => setShowAiDataPanel(false)}
          getTaskRows={() => applyAiReportScopeToEvidence({ rawSampleItems: taskJSON(1000), processSteps: apiProvider.processStepsTable() || [], variants: apiProvider.variantsTable() || [], scope: effectiveAiReportScope }).rawSampleItems}
          getPrivacyRules={getAttributePrivacyRules}
          applySampleItemPrivacyFn={applySampleItemPrivacy}
          totalItems={getModelMetrics().reduce((mx, m) => Math.max(mx, m.work_items || 0), 0)}
        />
        {/* ── Pre-generate panel ── */}
        {!generated && (
          <div style={{
            background: '#fff', padding: '28px 32px', margin: '0 10px 10px',
            borderRadius: '0 0 8px 8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            border: '2px solid #dfe1e6', borderTop: 'none',
          }}>
            {/* Description */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{
                width: 56, height: 56, margin: '0 auto 16px',
                background: 'linear-gradient(135deg, #0052cc 0%, #2684ff 100%)',
                borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Activity size={28} color="white" />
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: 18, fontWeight: 700, color: '#172b4d' }}>Generate Process Health</h3>
              <p style={{ margin: 0, fontSize: 14, color: '#5e6c84', lineHeight: 1.6, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
                A concise executive view of process health, operating risk, and where value is being lost.
                Explains what is happening, why it matters commercially, and which few issues deserve leadership attention first.
              </p>
            </div>

            {/* Generate button */}
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={generate}
                disabled={isGenerating}
                style={{
                  background: isGenerating ? '#dfe1e6' : 'linear-gradient(135deg, #0052cc 0%, #2684ff 100%)',
                  color: isGenerating ? '#97a0af' : '#fff',
                  border: 'none', padding: '10px 28px', borderRadius: 6,
                  fontSize: 15, fontWeight: 600,
                  cursor: isGenerating ? 'not-allowed' : 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  boxShadow: isGenerating ? 'none' : '0 2px 6px rgba(0,82,204,0.3)',
                }}
              >
                <Activity size={18} />
                {isGenerating ? 'Generating…' : 'Generate Process Health'}
              </button>
            </div>
          </div>
        )}

        {/* ── Assessment output ── */}
        {generated && assessmentHtml && (
          <div style={{ margin: '0 10px 0' }}>
            <HtmlPreviewFrame html={assessmentHtml} title="Process Health" />
          </div>
        )}

        <div style={{ height: 16 }} />
      </div>

      <ConsultantQaPanel
        isOpen={qaOpen}
        onClose={() => setQaOpen(false)}
        tabId="process-health"
        tabLabel="Process Health"
        themeColor="#0052CC"
        resultData={getCachedConsultantJson({ modelId, column: COL_ASSESSMENT })}
        savedHtml={assessmentHtml}
        generated={generated}
        modelTitle={getProcessModelTitle()}
        processLabel={processLabel}
        entityLabel={entityLabel}
        aiModelConfig={aiModelConfig}
      />

      <SaveSnapshotModal
        isOpen={snapshotModalOpen}
        onClose={() => setSnapshotModalOpen(false)}
        onSave={handleSaveSnapshot}
        tab="processHealth"
        tabLabel="Process Health"
        saving={snapshotSaving}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        title="Export Process Health"
        onExportHTML={handleExportHTML}
        onExportPDF={handleExportPDF}
        onExportJSON={handleExportJSON} onExportPPTX={handleExportPPTX}
      />
    </div>
  );
};

export default ProcessHealth;
